/**
 * Single source of truth for Ecospora's five circular loops.
 * Consumed by Home (grid), Impact (detail sections), Investors (moat cards).
 *
 * @typedef {"running" | "building"} LoopStatus
 *
 * @typedef {Object} CircularLoop
 * @property {string} id              Anchor id used on Impact page (e.g. "loop-01")
 * @property {string} number          Display number, zero-padded
 * @property {string} name            Loop name (e.g. "The Supply Loop")
 * @property {LoopStatus} status
 * @property {string} statusLabel     Human label for the pill
 * @property {string} [partner]       Partner organisation, if any
 * @property {string} shortDescription One-line copy for homepage cards
 * @property {string} longDescription  2-3 sentence copy for Impact sections
 * @property {string[]} flow          Ordered steps for diagram
 * @property {string} moatAngle       Why this loop is defensible (Investors page)
 */

/** @type {CircularLoop[]} */
export const loops = [
  {
    id: "loop-01",
    number: "01",
    name: "The Supply Loop",
    status: "running",
    statusLabel: "Running today",
    shortDescription:
      "Vertically integrated from kenaf to seed to mushroom — no dependency on external substrate or seed suppliers.",
    longDescription:
      "Most mushroom growers depend on outside suppliers for substrate and seed — every link is a point of failure. Ecospora has built the supply chain in-house: we grow our own kenaf, produce our own beglog, and propagate our own mushroom seed. The result is consistency, quality control, and full traceability from soil to harvest.",
    flow: ["Own kenaf", "Own beglog", "Own seed", "Mushroom harvest"],
    moatAngle:
      "Vertical integration from kenaf to seed eliminates external dependency, reduces input cost, and ensures full traceability.",
  },
  {
    id: "loop-02",
    number: "02",
    name: "The Community Loop",
    status: "running",
    statusLabel: "Running today",
    partner: "LKTN",
    shortDescription:
      "LKTN-funded beglog reaches asnaf families. They grow. We buy back. Income flows into communities who need it most.",
    longDescription:
      "In partnership with Lembaga Kenaf dan Tembakau Negara, we supply beglog to asnaf families across Malaysia. They grow the mushrooms at home. We guarantee buyback at fair price. The mushrooms reach the market through our brands. This isn't a CSR programme — it's structurally embedded into how Ecospora sources.",
    flow: ["LKTN funds beglog", "Asnaf grow", "Ecospora buys back", "Market", "Reinvest"],
    moatAngle:
      "Government-backed (LKTN), structurally embedded into the supply chain. Not a CSR add-on, and not easily copied.",
  },
  {
    id: "loop-03",
    number: "03",
    name: "The Commercial Loop",
    status: "running",
    statusLabel: "Running today",
    shortDescription:
      "Agency and retail surplus loops back into product lines — minimising waste at the point of sale.",
    longDescription:
      "Returns and surplus from our agency and retail partners don't go to waste. We've built reverse logistics and reintegration processes so that unsold inventory finds a second life — whether through processing into mushroom-based food products, redistribution through alternative channels, or composting into the soil loop.",
    flow: ["Agency / retail sales", "Returns & surplus", "Reintegration", "Second-life products"],
    moatAngle:
      "Reverse logistics and product reintegration capability — most growers can't recover surplus.",
  },
  {
    id: "loop-04",
    number: "04",
    name: "The Soil Loop",
    status: "building",
    statusLabel: "In development with UPM",
    partner: "UPM",
    shortDescription:
      "Spent mushroom substrate becomes agricultural compost — closing the loop back to kenaf farms.",
    longDescription:
      "Spent mushroom substrate — the leftover material after harvest — is typically treated as waste. With Universiti Putra Malaysia, we're developing a closed-loop composting process that converts substrate into agricultural-grade fertiliser, which goes back to the kenaf farms that started the cycle. The loop closes underground.",
    flow: ["Spent substrate", "UPM composting", "Agricultural compost", "Back to kenaf farms"],
    moatAngle:
      "UPM-validated composting process — first-mover advantage on closed substrate loop.",
  },
  {
    id: "loop-05",
    number: "05",
    name: "The Material Loop",
    status: "building",
    statusLabel: "In development with USM",
    partner: "USM",
    shortDescription:
      "Mushroom mycelium grown into biodegradable packaging — replacing single-use plastic.",
    longDescription:
      "With Universiti Sains Malaysia's mycology lab, we're growing packaging from mushroom mycelium. Fully biodegradable, locally manufacturable, and cost-competitive against single-use plastics. When this loop reaches production, every mushroom we grow can carry itself to market in a container made of itself.",
    flow: ["Mushroom mycelium", "Grown into packaging", "Used and returned", "Biodegrades into soil"],
    moatAngle:
      "USM-developed mycelium packaging — adjacent category expansion path with academic IP.",
  },
];

export const runningLoops = loops.filter((l) => l.status === "running");
export const buildingLoops = loops.filter((l) => l.status === "building");
